<script setup>
import {
  computed,
  ref,
  watch,
  inject,
  onMounted,
  onUpdated,
  nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import RodizioCountdown from './RodizioCountdown.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import wootConstants from 'dashboard/constants/globals';
import {
  viewerOwnsPersonalInboxUnreadState,
  shouldPreserveAssignedUnreadForBox,
  viewerOwnsUnreadState,
} from 'dashboard/helper/unreadOwnership';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';
import { isTeamNumberContact } from 'dashboard/helper/teamNumber';
import TeamNumberBadge from 'dashboard/components/widgets/TeamNumberBadge.vue';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AceleresOwnershipTransferModal from './AceleresOwnershipTransferModal.vue';
import { isAceleresConversationStarted } from 'dashboard/helper/aceleresConversationState';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  activeAssigneeTab: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignSelf',
  'startConversation',
  'assignMine',
  'assignPersonal',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const PERSONAL_FUNNEL_LABELS = new Set([
  'nao-contactado',
  'ia',
  'contato-inicial',
  'desenvolvimento',
  'negociacao',
  'link-pagamento-gerado',
  'venda-feita',
  'nao-qualificado',
  'perdido',
  'sem-atendimento',
]);

const router = useRouter();
const store = useStore();

const { getPlainText } = useMessageFormatter();

const conversationCard = ref(null);
const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });
const ownershipStatus = ref(null);
const showOwnershipModal = ref(false);
const ownershipSubmitting = ref(false);
// Reset UI state when conversation changes at same index (no :key, instance reused on reorder)
// This prevents context menu/hover state from leaking to a different conversation
// Emit contextMenuToggle(false) to sync parent state if menu was open during recycling
const resetState = () => {
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  showOwnershipModal.value = false;
  ownershipStatus.value = null;
  contextMenu.value = { x: null, y: null };
};

watch(() => props.chat.id, resetState);

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const currentUser = useMapGetter('getCurrentUser');
const inboxId = computed(() => props.chat.inbox_id);
const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

// Aceleres: nome do vendedor responsável exibido no card, com fallback
// explícito quando a conversa está na fila de novos (sem responsável) —
// evita que o nome da instância seja lido como identificação do vendedor.
const assigneeDisplayName = computed(
  () => assignee.value.name || 'Sem vendedor responsável'
);

const sender = computed(() => chatMetadata.value.sender || {});

const senderId = computed(() => sender.value.id);

const currentContact = computed(() => {
  const contact = senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : null;

  // A resposta da conversa já traz sender. Usá-lo enquanto o store de
  // contatos termina de hidratar evita cards sem nome ou avatar no primeiro
  // paint (e também cobre conversas legadas sem contato associado).
  return contact?.id ? contact : sender.value;
});

const contactName = computed(
  () => currentContact.value?.name || 'Contato indisponível'
);

// Aceleres -- selo "Número da Equipe" na listagem lateral de conversas.
const isTeamNumber = computed(() => isTeamNumberContact(currentContact.value));

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const ownsUnreadState = computed(() => {
  const inboxAttributes = inbox.value?.additional_attributes || {};
  return viewerOwnsUnreadState({
    currentUserId: currentUserId.value,
    assigneeId: assignee.value?.id,
    isConversationStarted: isAceleresConversationStarted(props.chat),
    // In Outros/Pessoais the backend intentionally exposes the assignee's
    // unread state to managers and supervisors. Keep the green indicator in
    // the card instead of hiding it because the assignee is another user.
    preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
      props.activeAssigneeTab
    ),
    personalInboxAttributes: {
      ...inboxAttributes,
      connection_type:
        inboxAttributes.connection_type || props.chat?.inbox_connection_type,
      owner_chatwoot_user_id:
        inboxAttributes.owner_chatwoot_user_id ||
        props.chat?.inbox_owner_chatwoot_user_id,
    },
    personalConversationOwnerId:
      props.chat?.additional_attributes?.personal_owner_chatwoot_user_id,
  });
});

const ownsPersonalInboxUnreadState = computed(() => {
  const inboxAttributes = inbox.value?.additional_attributes || {};
  return viewerOwnsPersonalInboxUnreadState({
    currentUserId: currentUserId.value,
    personalInboxAttributes: {
      ...inboxAttributes,
      connection_type:
        inboxAttributes.connection_type || props.chat?.inbox_connection_type,
      owner_chatwoot_user_id:
        inboxAttributes.owner_chatwoot_user_id ||
        props.chat?.inbox_owner_chatwoot_user_id,
    },
    personalConversationOwnerId:
      props.chat?.additional_attributes?.personal_owner_chatwoot_user_id,
  });
});

const unreadCount = computed(() => {
  if (!ownsUnreadState.value) {
    return 0;
  }

  return props.chat.unread_count || 0;
});

const hasUnread = computed(() => unreadCount.value > 0);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

// Aceleres: preview do ultimo mensaje no hover (title nativo do Chrome).
// Hover nao marca como lido (makeMessagesRead so roda no click).
const unreadPreview = computed(() => {
  const msg = lastMessageInChat.value;
  if (!msg) return '';
  const subject = msg.content_attributes?.email?.subject;
  const text = getPlainText(subject || msg.content || '').trim();
  return text;
});

const rodizioAssignedAt = computed(() => {
  // Aceleres: timer só enquanto o lead ainda circula no rodízio — conversa
  // ou não-aberta não mostram countdown.
  const attrs = props.chat?.additional_attributes || {};
  if (attrs.rodizio_locked || attrs.rodizio_exhausted) return null;
  if (props.chat?.status && props.chat.status !== 'open') return null;
  return attrs.rodizio_assigned_at || null;
});

const rodizioIdleMins = computed(() => {
  const attrs = props.chat?.additional_attributes || {};
  const value = Number(attrs.rodizio_idle_minutes || attrs.rodizio_idle_mins);
  return Number.isFinite(value) && value > 0 ? value : null;
});

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const isTrainingChat = computed(() =>
  isTrainingConversation(props.chat, currentUser.value)
);

const inboxConnectionType = computed(
  () =>
    props.chat.inbox_connection_type ||
    inbox.value?.additional_attributes?.connection_type ||
    ''
);

const pendingOwnershipRequest = computed(
  () => ownershipStatus.value?.pending_request || null
);

const currentPortfolioOwner = computed(
  () => ownershipStatus.value?.current_owner || null
);

const canRequestOwnership = computed(
  () => ownershipStatus.value?.can_request === true
);

const ownershipAgents = computed(() => {
  return ownershipStatus.value?.eligible_targets || [];
});

// Badge de origem da conversa + estado operacional atual da IA. O atributo
// atendido_ia permanece reservado ao filtro histórico.
const origemCanal = computed(() => {
  const ct = inbox.value?.channel_type;
  if (ct === 'Channel::Whatsapp') return { label: 'Oficial', kind: 'oficial' };
  if (ct === 'Channel::Api')
    return { label: 'WhatsApp Corporativo', kind: 'corporativo' };
  return null;
});

const iaAtiva = computed(() => {
  const v = props.chat.custom_attributes?.ia_ativa_agora;
  return v === true || v === 'true' || v === 1 || v === '1';
});

const showOrigemBadges = computed(() => !!origemCanal.value || iaAtiva.value);

const showInboxName = computed(() => {
  return (
    !props.hideInboxName &&
    !!inbox.value?.id &&
    [
      wootConstants.STATUS_TYPE.OPEN,
      wootConstants.STATUS_TYPE.RESOLVED,
    ].includes(props.chat?.status)
  );
});

const showConcludedStatusBadge = computed(
  () => props.chat?.status === wootConstants.STATUS_TYPE.RESOLVED
);

const showNotAttendedBadge = computed(() => {
  return (
    props.activeAssigneeTab === 'outros' &&
    assignee.value?.id &&
    Number(assignee.value.id) !== Number(currentUserId.value) &&
    !isAceleresConversationStarted(props.chat)
  );
});

const showMetaSection = computed(() => {
  return showInboxName.value || props.showAssignee || props.chat.priority;
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const isPersonalConversation = computed(() => {
  const labels = Array.isArray(props.chat.labels) ? props.chat.labels : [];
  const attrs = props.chat.additional_attributes || {};
  const inboxAttrs = inbox.value?.additional_attributes || {};

  return (
    labels.includes('pessoal') ||
    isAceleresIndividualOwn(inboxAttrs.connection_type) ||
    Number(attrs.personal_owner_chatwoot_user_id) > 0 ||
    Number(attrs.personal_origin_chatwoot_user_id) > 0 ||
    Number(attrs.owner_chatwoot_user_id) > 0
  );
});

const visibleConversationLabels = computed(() => {
  const labels = Array.isArray(props.chat.labels) ? props.chat.labels : [];
  if (!isPersonalConversation.value) return labels;

  return labels.filter(label => !PERSONAL_FUNNEL_LABELS.has(label));
});

const showLabelsSection = computed(() => {
  return visibleConversationLabels.value.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

// Aceleres: modo seleção (WhatsApp-style) injetado pelo ChatList — quando
// ativo, clique no card alterna seleção em vez de navegar.
const aclSelectionMode = inject('aclSelectionMode', ref(false));
const onToggleSelect = e => {
  if (e) e.stopPropagation();
  if (props.selected) {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('selectConversation', props.chat.id, inbox.value.id);
  }
};

const onCardClick = async e => {
  if (aclSelectionMode.value) {
    onToggleSelect(e);
    return;
  }
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  // O dono da inbox pessoal pode marcar a conversa como lida mesmo quando o
  // rodízio deixou outro vendedor no assignee. Supervisores em Outros não
  // entram neste fluxo porque a conversa não pertence a uma inbox pessoal.
  if (
    ownsPersonalInboxUnreadState.value &&
    Number(props.chat.unread_count) > 0
  ) {
    // Mark as read without blocking navigation to the conversation.
    store.dispatch('markMessagesRead', { id: props.chat.id }).catch(() => {});
  }

  store.dispatch('previewActiveChat', { data: props.chat });
  if (props.chat?.dataFetched !== true) {
    store.dispatch('getConversation', props.chat.id);
  }
  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const fetchOwnershipStatus = async conversationId => {
  try {
    const { data } =
      await ConversationTransferRequestsAPI.status(conversationId);
    if (Number(props.chat.id) === Number(conversationId)) {
      ownershipStatus.value = data?.payload || null;
    }
  } catch {
    ownershipStatus.value = null;
  }
};

const openContextMenu = async e => {
  e.preventDefault();
  e.stopPropagation();
  if (!props.enableContextMenu || isTrainingChat.value) return;

  const conversationId = props.chat.id;
  ownershipStatus.value = null;
  await fetchOwnershipStatus(conversationId);
  if (Number(props.chat.id) !== Number(conversationId)) return;

  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignSelf = () => {
  emit('assignSelf', props.chat.id);
  closeContextMenu();
};

const onStartConversation = () => {
  emit('startConversation', props.chat.id);
  closeContextMenu();
};

const onAssignMine = () => {
  emit('assignMine', props.chat.id);
  closeContextMenu();
};

const onAssignPersonal = () => {
  emit('assignPersonal', props.chat.id);
  closeContextMenu();
};

const onRequestOwnershipTransfer = async () => {
  closeContextMenu();
  await fetchOwnershipStatus(props.chat.id);

  if (!ownershipAgents.value.length) {
    useAlert('Nenhum outro vendedor disponível para receber a carteirização');
    return;
  }

  showOwnershipModal.value = true;
};

const submitOwnershipRequest = async ({ targetAssigneeId, justification }) => {
  ownershipSubmitting.value = true;
  try {
    const { data } = await ConversationTransferRequestsAPI.create({
      conversationId: props.chat.id,
      targetAssigneeId,
      justification,
    });
    showOwnershipModal.value = false;
    await fetchOwnershipStatus(props.chat.id);
    useAlert(
      data?.already_pending
        ? 'Já existe uma alteração de carteirização aguardando autorização'
        : 'Solicitação de alteração de carteirização enviada ao gestor'
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível solicitar a alteração de carteirização'
    );
  } finally {
    ownershipSubmitting.value = false;
  }
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};

const applyAclOrigemLayout = () => {
  const root = conversationCard.value;
  if (!root) return;

  root.querySelectorAll('.acl-origem-row').forEach(row => {
    row.style.position = 'relative';
    row.style.paddingRight = '24px';
    row.style.display = 'flex';
    row.style.flexWrap = 'wrap';
    row.style.alignItems = 'flex-start';
    row.style.alignContent = 'flex-start';
    row.style.gap = '4px';
  });

  root.querySelectorAll('.acl-origem-row button').forEach(btn => {
    btn.style.setProperty('display', 'inline-flex', 'important');
    btn.style.setProperty('visibility', 'visible', 'important');
    btn.style.setProperty('pointer-events', 'auto', 'important');
    btn.style.setProperty('position', 'absolute', 'important');
    btn.style.setProperty('top', '0', 'important');
    btn.style.setProperty('right', '0', 'important');
    btn.style.setProperty('margin', '0', 'important');
    btn.style.setProperty('z-index', '3', 'important');
    btn.style.setProperty('width', '20px', 'important');
    btn.style.setProperty('height', '20px', 'important');
    btn.removeAttribute('aria-hidden');
    btn.tabIndex = 0;
  });
};

onMounted(() => {
  nextTick(() => applyAclOrigemLayout());
});

onUpdated(() => {
  nextTick(() => applyAclOrigemLayout());
});
</script>

<template>
  <div
    ref="conversationCard"
    class="acl-conversation-card relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 border-t-0 border-b-0 border-l-0 border-r-0 border-transparent border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :class="{
      'active animate-card-select bg-n-background border-n-weak my-1 rounded-md':
        isActiveChat,
      'bg-n-slate-2': selected,
      'px-0': compact,
      'px-3': !compact,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- Aceleres: check verde à esquerda do avatar quando em modo seleção
         (WhatsApp-style). Substitui o overlay de checkbox no próprio avatar. -->
    <button
      v-if="aclSelectionMode && !hideThumbnail"
      type="button"
      class="acl-sel-check"
      :class="{ 'acl-sel-check--on': selected }"
      :aria-pressed="selected"
      :aria-label="selected ? 'Desmarcar' : 'Selecionar'"
      @click.stop="onToggleSelect"
    >
      <svg
        v-if="selected"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </button>
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="contactName"
        :src="currentContact.thumbnail"
        :size="49"
        :status="currentContact.availability_status"
        class="avatar-conversa"
        :class="[!showInboxName ? 'mt-4' : 'mt-8']"
        hide-offline-status
        rounded-full
      />
    </div>
    <div :title="unreadPreview" class="px-0 py-3 flex-1 min-w-0">
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-center gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center whitespace-nowrap"
            :class="
              assignee.name
                ? 'truncate max-w-[170px] sm:max-w-[210px]'
                : 'text-n-slate-9 flex-shrink-0'
            "
            :title="assigneeDisplayName"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
            {{ assigneeDisplayName }}
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
        </div>
      </div>
      <div class="flex items-center gap-1 mx-2 flex-1 min-w-0 ltr:pr-16 rtl:pl-16">
        <h4
          class="conversation--user text-sm my-0 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap min-w-0 text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ contactName }}
        </h4>
        <TeamNumberBadge v-if="isTeamNumber" compact />
      </div>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
        <span
          class="shadow-lg rounded-full text-[11px] font-semibold w-5 h-5 ltr:ml-auto rtl:mr-auto mt-1 text-white bg-n-teal-9 inline-flex items-center justify-center leading-none"
          :class="hasUnread ? 'block' : 'hidden'"
        >
          {{ unreadCount }}
        </span>
        <RodizioCountdown
          v-if="rodizioAssignedAt"
          :assigned-at="rodizioAssignedAt"
          :idle-mins="rodizioIdleMins"
          compact
          class="mt-1 ltr:ml-auto rtl:mr-auto"
        />
      </div>
      <div
        v-if="showOrigemBadges || showNotAttendedBadge || showLabelsSection"
        class="acl-origem-row flex items-center flex-wrap gap-1 mt-1 mb-0 mx-2"
      >
        <span
          v-if="origemCanal"
          class="inline-flex items-center gap-1 rounded-full px-1.5 h-[20px] leading-none text-[12px] font-semibold"
          :class="
            origemCanal.kind === 'oficial'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-blue-3 text-n-blue-11'
          "
        >
          <fluent-icon
            :icon="
              origemCanal.kind === 'oficial' ? 'checkmark-circle' : 'whatsapp'
            "
            size="14"
          />
          {{ origemCanal.label }}
        </span>
        <span
          v-if="iaAtiva"
          class="inline-flex items-center gap-1 rounded-full px-1.5 h-[20px] leading-none text-[12px] font-semibold bg-n-amber-3 text-n-amber-11"
        >
          <fluent-icon icon="bot" size="14" />
          {{ 'IA' }}
        </span>
        <span
          v-if="showConcludedStatusBadge"
          class="inline-flex items-center rounded-full px-2 h-[20px] leading-none text-[12px] font-semibold bg-n-teal-9 text-white"
        >
          {{ $t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT') }}
        </span>
        <span
          v-if="showNotAttendedBadge"
          class="inline-flex items-center rounded-full px-2 h-[20px] leading-none text-[12px] font-semibold bg-n-amber-3 text-n-amber-11"
        >
          {{ $t('CHAT_LIST.NOT_ATTENDED') }}
        </span>
        <CardLabels
          v-if="showLabelsSection"
          :conversation-labels="visibleConversationLabels"
          class="inline-flex"
        >
          <template v-if="hasSlaPolicyId" #before>
            <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
          </template>
        </CardLabels>
      </div>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :team-id="
          chat.team_id ||
          chat.team?.id ||
          chat.meta?.team?.id ||
          chat.inbox?.team_id ||
          teamId
        "
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        :active-assignee-tab="activeAssigneeTab"
        :assignee-id="assignee?.id"
        :current-user-id="currentUserId"
        :additional-attributes="chat.additional_attributes"
        :conversation-status="chat.status"
        :inbox-connection-type="inboxConnectionType"
        :can-request-ownership="canRequestOwnership"
        :ownership-request-pending="!!pendingOwnershipRequest"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-self="onAssignSelf"
        @start-conversation="onStartConversation"
        @assign-mine="onAssignMine"
        @assign-personal="onAssignPersonal"
        @request-ownership-transfer="onRequestOwnershipTransfer"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
    <AceleresOwnershipTransferModal
      :show="showOwnershipModal"
      :current-owner="currentPortfolioOwner"
      :agents="ownershipAgents"
      :submitting="ownershipSubmitting"
      @close="showOwnershipModal = false"
      @submit="submitOwnershipRequest"
    />
  </div>
</template>

<style lang="scss" scoped>
/* Aceleres: chat labels (woot-label) renderizados em linha com os badges
   de origem, restyled como pill preto sem o ponto colorido. */
.acl-origem-row :deep(.label) {
  border-radius: 50px;
  background-color: #000;
  color: #fff;
  border-color: #000;
  height: 18px;
  padding: 0 8px;
  line-height: 1;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
}
.acl-origem-row :deep(.label .label-color-dot),
.acl-origem-row :deep(.label .label--icon),
.acl-origem-row :deep(.label .label-action--button),
.acl-origem-row :deep(.label svg) {
  display: none;
}
.acl-origem-row :deep(.label a) {
  color: #fff;
}

/* Aceleres: check verde (modo seleção WhatsApp) à esquerda do avatar */
.acl-sel-check {
  flex-shrink: 0;
  align-self: center;
  width: 22px;
  height: 22px;
  margin-right: 20px;
  border-radius: 6px;
  background-color: #ffffff;
  border: 1.8px solid #b8b8b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  cursor: pointer;
  transition:
    background-color 0.12s,
    border-color 0.12s,
    color 0.12s;
  padding: 0;
}
.acl-sel-check:hover {
  border-color: #1fad54;
}
.acl-sel-check--on {
  background-color: #1fad54;
  border-color: #1fad54;
  color: #ffffff;
}
.acl-sel-check--on:hover {
  background-color: #189547;
  border-color: #189547;
}
</style>
